/* eslint-disable camelcase */
import React from 'react';

import styled from 'styled-components';

import FilterIcon from '~/icons/components/FilterIcon';
import FormattedMessage from '~/techstyle-shared/react-intl/src/FormattedMessage/FormattedMessage';

import {
  haloRingBase,
  haloRingHover,
  haloRingPressed,
} from '../../styles/haloRing';
import SortDropdown from '../SortDropdown';

import QuickFilter from './QuickFilter';

export const StyledFilterButton = styled.button`
  padding: 0px 16px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  border: 0.5px solid ${({ theme }) => theme.colors.gridV2FilterBtnBorder};
  border-radius: 4px;
  background-color: transparent;
  cursor: pointer;
  font-family: inherit;
  font-size: 14px;
  transition: background-color 0.2s;

  &:hover {
    background-color: ${({ theme }) => theme.colors.gridV2FilterBtnBgHover};
  }

  &:active {
    background-color: ${({ theme }) => theme.colors.gridV2FilterBtnBgActive};
  }
`;

const StyledSeparator = styled.div`
  width: 1px;
  height: 32px;
  background-color: ${({ theme }) => theme.colors.gridV2FilterSeparator};
`;

const FiltersContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const FiltersList = styled.ul`
  display: flex;
  align-items: center;
  gap: 8px;
  list-style: none;
`;

const ClearAllButton = styled.button`
  position: relative;
  padding: 8px;
  border-radius: 64px;
  border: 1px solid transparent;
  background-color: transparent;
  cursor: pointer;
  transition: border-color 0.2s;
  font-size: 14px;
  line-height: 24px;
  font-family: inherit;
  ${haloRingBase};

  &::before {
    border-radius: inherit;
  }

  &:hover {
    border-color: transparent;
  }

  &:active {
    border-color: transparent;
  }

  &:hover::before {
    ${haloRingHover};
  }

  &:active::before {
    ${haloRingPressed};
  }
`;

interface FilterControlsProps {
  openFiltersPanel: () => void;
  expandFilterDropdown: (facetName: string) => void;
  // eslint-disable-next-line camelcase
  facets: Array<{ name: string; display_name: string; options?: Array<any> }>;
  maxNumQuickFiltersToRender: number;
  selectedFacets: Array<{
    facetName: string;
    urlName: string;
    facetOption: string;
    facetOptionDisplayName: string;
  }>;
  handleClearAllFilters: () => void;
}

const FilterControls: React.FC<FilterControlsProps> = ({
  openFiltersPanel,
  expandFilterDropdown,
  facets,
  maxNumQuickFiltersToRender,
  selectedFacets,
  handleClearAllFilters,
}) => {
  const getSelectedOptionsCount = (filterName: string) => {
    // some options like color have the matching filterName under facetName,
    // while others like available_size_top have the matching filterName under urlName,
    // so we check both to be safe.
    return (
      selectedFacets?.filter(
        (filter) =>
          filter.facetName === filterName || filter.urlName === filterName
      )?.length || 0
    );
  };

  return (
    <FiltersContainer>
      <StyledFilterButton type="button" onClick={openFiltersPanel}>
        <FilterIcon />
        <FormattedMessage
          id="grid_static.filter_button"
          defaultMessage="Filter"
        />
      </StyledFilterButton>

      <StyledSeparator />

      <FiltersList>
        {facets
          ?.slice(0, maxNumQuickFiltersToRender)
          ?.map(({ name, display_name }) => (
            <li key={name}>
              <QuickFilter
                name={name}
                display_name={display_name}
                onClick={() => {
                  openFiltersPanel();
                  expandFilterDropdown(name);
                }}
                numSelectedOptions={getSelectedOptionsCount(name)}
              />
            </li>
          ))}
      </FiltersList>

      {selectedFacets?.length > 0 && (
        <ClearAllButton
          type="button"
          onClick={handleClearAllFilters}
          data-autotag="filter-clear-all"
        >
          <FormattedMessage
            id="grid_static.clear_all"
            defaultMessage="Clear All"
          />
        </ClearAllButton>
      )}

      <div style={{ marginLeft: 'auto' }}>
        <SortDropdown />
      </div>
    </FiltersContainer>
  );
};

export default FilterControls;
